import { useState, useEffect } from 'react';

import TextInput from 'components/form/textInput';

import * as S from './styles';

interface TableComponentProps<t> {
  data: t[] | null;
  onDelete?: (e: any, item: t) => void;
  onSelect?: (e: any, index: number | null, item: any) => void;
  defaultIndex?: number | string | null;
  setUpdateIndex?: any;
  handleOptionsChange?: any;
}

/**
 * The saved options of a single-select customization.
 *
 * It was a `<table>` with `border-spacing: 0 0.8rem`, white cells, a
 * hand-rolled radio out of a bordered div and an SVG, and a `#fdf0ef` circular
 * delete button — none of it on the palette. It is a list now: one bordered
 * row per option, the two languages side by side in the same grid the rest of
 * the product pairs them in, and the two controls in fixed slots so they line
 * up with their column headings all the way down.
 */
const Table = <t extends { [key: string]: any }>({
  data,
  onDelete,
  onSelect,
  defaultIndex,
  setUpdateIndex,
  handleOptionsChange
}: TableComponentProps<t>) => {
  const initialIndex =
    typeof defaultIndex === 'number'
      ? defaultIndex
      : typeof defaultIndex === 'string' && !isNaN(+defaultIndex)
        ? +defaultIndex
        : null;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(initialIndex);
  const [filteredData, setFilteredData] = useState<any>();

  const handleSelect = (e: any, index: number | null, item: any) => {
    setSelectedIndex(index);
    if (onSelect) {
      onSelect(e, index, item);
    }
  };

  const handleChange = (e: any, item: any) => {
    const { name, value } = e.target;

    const updatedItems = filteredData?.map((i: any) => {
      if (
        i.translations?.[0]?.translate === item?.translations?.[0]?.translate &&
        i.translations?.[1]?.translate === item?.translations?.[1]?.translate
      ) {
        let updatedItem = { ...i };

        if (name === 'en-title') {
          updatedItem.englishOption = value;

          updatedItem.translations = updatedItem.translations.map((t: any) =>
            t.language === 'en' ? { ...t, translate: value } : t
          );
        } else if (name === 'ar-title') {
          updatedItem.arabicOption = value;

          updatedItem.translations = updatedItem.translations.map((t: any) =>
            t.language === 'ar' ? { ...t, translate: value } : t
          );
        }

        return updatedItem;
      }
      return i;
    });

    setFilteredData(updatedItems);

    if (handleOptionsChange) {
      handleOptionsChange(e, item);
    }
  };

  useEffect(() => {
    if (data) {
      setFilteredData(
        data
          ?.filter((item) => !item?.deleted)
          .map((item) => {
            const englishOption = item.translations.find((t: any) => t.language === 'en')?.translate || '';
            const arabicOption = item.translations.find((t: any) => t.language === 'ar')?.translate || '';

            return {
              ...item,
              englishOption,
              arabicOption
            };
          })
      );
    }
  }, [data]);

  useEffect(() => {
    if (initialIndex) setSelectedIndex(initialIndex);
  }, [defaultIndex, initialIndex]);

  useEffect(() => {
    const defaultIndex = filteredData?.findIndex((item: any) => item?.isDefault);
    setSelectedIndex(defaultIndex !== undefined && defaultIndex !== -1 ? defaultIndex : null);
    if (setUpdateIndex) setUpdateIndex(defaultIndex !== undefined && defaultIndex !== -1 ? defaultIndex : null);
  }, [filteredData, setSelectedIndex, setUpdateIndex]);

  if (!filteredData?.length) return <></>;

  return (
    <S.List>
      <S.Head>
        <span>Option EN</span>
        <span>Option AR</span>
        {onSelect ? <span>Default</span> : <></>}
        {onDelete ? <span /> : <></>}
      </S.Head>

      {filteredData.map((row: any, index: number) => (
        <S.Row key={index}>
          <TextInput name="en-title" value={row?.englishOption} onChange={(e) => handleChange(e, row)} />
          <TextInput name="ar-title" value={row?.arabicOption} dir="rtl" onChange={(e) => handleChange(e, row)} />

          {onSelect ? (
            <S.Slot>
              <S.Radio className={selectedIndex === index ? 'on' : ''} title="Make this the default">
                <input
                  type="radio"
                  name="defaultOption"
                  checked={selectedIndex === index}
                  onChange={(e) => handleSelect(e, selectedIndex, row)}
                />
              </S.Radio>
            </S.Slot>
          ) : (
            <></>
          )}

          {onDelete ? (
            <S.Slot>
              <S.Delete type="button" onClick={(e) => onDelete(e, row)} aria-label="Delete option">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round">
                  <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
                </svg>
              </S.Delete>
            </S.Slot>
          ) : (
            <></>
          )}
        </S.Row>
      ))}
    </S.List>
  );
};

export default Table;
